import { Button } from "@chakra-ui/react";
import { ConfirmDestructiveActionDialog } from "../components/modals/ConfirmDestructiveActionDialog";
import { useCallback, useMemo, useState } from "react";
import { WarningTwoIcon } from "@chakra-ui/icons";

interface BaseProps {
  icon: React.ReactElement;
  color: string;
  title: string;
  variant?: "solid" | "link";
  isDisabled?: boolean;
}

interface NonDestructiveActionButtonProps extends BaseProps {
  onClick: () => void;
  isDestructive?: false;
  confirmTitle?: never;
  confirmDescription?: never;
}

interface DestructiveActionButtonProps extends BaseProps {
  onClick: (confirmed: boolean) => void;
  isDestructive: true;
  confirmTitle: string;
  confirmDescription: React.ReactNode;
}

export function ActionButton({
  icon,
  color,
  onClick,
  title,
  variant = "solid",
  isDisabled = false,
  isDestructive,
  confirmTitle,
  confirmDescription,
}: NonDestructiveActionButtonProps | DestructiveActionButtonProps) {
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);

  const textColor = useMemo(
    () => (variant === "link" ? color : undefined),
    [variant, color],
  );
  const bgColor = useMemo(
    () => (variant === "solid" ? color : undefined),
    [variant, color],
  );

  const handleOnClick = useCallback(() => {
    if (isDestructive) {
      setIsConfirmDialogOpen(true);
    } else {
      onClick();
    }
  }, [isDestructive, onClick]);

  if (!isDestructive) {
    return (
      <Button
        bgColor={bgColor}
        color={textColor}
        isDisabled={isDisabled}
        leftIcon={icon}
        onClick={handleOnClick}
        variant={variant}
      >
        {title}
      </Button>
    );
  }

  return (
    <>
      <ConfirmDestructiveActionDialog
        icon={<WarningTwoIcon color="yellow.500" mr={2} />}
        isOpen={isConfirmDialogOpen}
        onConfirm={(confirmed) => {
          setIsConfirmDialogOpen(false);
          onClick(confirmed);
        }}
        title={confirmTitle}
      >
        {confirmDescription}
      </ConfirmDestructiveActionDialog>
      <Button
        bgColor={bgColor}
        color={textColor}
        isDisabled={isDisabled}
        leftIcon={icon}
        onClick={handleOnClick}
        variant={variant}
      >
        {title}
      </Button>
    </>
  );
}
